import { useQuery } from "@tanstack/react-query";
import {
  Users,
  UsersRound,
  FolderKanban,
  Key,
  Loader2,
  Activity,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { adminService } from "@/services/admin-service";
import { StatCard } from "@/components/ui/StatCard";
import { Card, CardHeader, CardBody } from "@/components/ui/Card";

export default function AdminDashboardPage() {
  // Fetch real data from APIs
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => adminService.listUsers(),
  });

  // Extract users array from paginated response
  const users = usersResponse?.users || [];

  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminService.listTeams(),
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => adminService.listProjects(),
  });

  const { data: apiKeys, isLoading: keysLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => adminService.listAPIKeys(),
  });

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative">
          <h1 className="mb-2 text-4xl font-bold">Welcome back!</h1>
          <p className="text-lg text-blue-100">
            Here's what's happening with your Atlas Guard system today.
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={usersResponse?.total || 0}
          icon={Users}
          loading={usersLoading}
          href="/admin/users"
          gradient="from-blue-400 to-blue-600"
        />
        <StatCard
          title="Active Teams"
          value={teams?.length || 0}
          icon={UsersRound}
          loading={teamsLoading}
          href="/admin/teams"
          gradient="from-green-400 to-green-600"
        />
        <StatCard
          title="Projects"
          value={projects?.length || 0}
          icon={FolderKanban}
          loading={projectsLoading}
          href="/admin/projects"
          gradient="from-orange-400 to-orange-600"
        />
        <StatCard
          title="API Keys"
          value={apiKeys?.length || 0}
          icon={Key}
          loading={keysLoading}
          href="/admin/api-keys"
          gradient="from-purple-400 to-purple-600"
        />
      </div>

      {/* Dashboard Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="space-y-6 lg:col-span-2">
          <RecentUsers users={users} loading={usersLoading} />
          <RecentProjects projects={projects} loading={projectsLoading} />
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          <ActivitySummary
            users={users}
            teams={teams}
            projects={projects}
            apiKeys={apiKeys}
          />
        </div>
      </div>
    </div>
  );
}

function ActivitySummary({
  users,
  teams,
  projects,
  apiKeys,
}: {
  users?: any[];
  teams?: any[];
  projects?: any[];
  apiKeys?: any[];
}) {
  const activeUsers =
    users?.filter((user) => user.status === "active").length || 0;
  const activeProjects =
    projects?.filter((project) => project.status === "active").length || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Activity Summary
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Current system overview
            </p>
          </div>
          <div className="rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 p-2">
            <Activity className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Active Users
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {activeUsers}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-2 w-2 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Total Teams
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {teams?.length || 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-2 w-2 rounded-full bg-orange-500"></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Active Projects
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {activeProjects}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-2 w-2 rounded-full bg-purple-500"></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                API Keys
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {apiKeys?.length || 0}
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function RecentUsers({ users, loading }: { users?: any[]; loading: boolean }) {
  if (loading) {
    return (
      <Card className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </Card>
    );
  }

  const recentUsers = users?.slice(0, 5) || [];

  return (
    <Card hover>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Recent Users
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Latest registered users
            </p>
          </div>
          <div className="rounded-xl bg-gradient-to-r from-green-400 to-green-600 p-2">
            <Users className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {recentUsers.length === 0 ? (
          <div className="py-8 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No users yet
            </p>
            <p className="text-xs text-gray-400">
              Users will appear here once registered
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentUsers.map((user) => (
              <div
                key={user.user_id}
                className="flex items-center space-x-4 rounded-xl bg-gray-50/50 p-3 transition-colors hover:bg-gray-100/50 dark:bg-gray-800/50 dark:hover:bg-gray-700/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-400 to-blue-600 font-semibold text-white">
                  {user.user_name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {user.user_name}
                  </p>
                  <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                    {user.user_email}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                      user.status === "active"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
                    )}
                  >
                    {user.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex items-center justify-between">
          <a
            href="/admin/users"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            View all users
            <svg
              className="ml-1 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Showing {recentUsers.length} of {users?.length || 0}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

function RecentProjects({
  projects,
  loading,
}: {
  projects?: any[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </Card>
    );
  }

  const recentProjects = projects?.slice(0, 5) || [];

  return (
    <Card hover>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Recent Projects
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Latest created projects
            </p>
          </div>
          <div className="rounded-xl bg-gradient-to-r from-orange-400 to-orange-600 p-2">
            <FolderKanban className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {recentProjects.length === 0 ? (
          <div className="py-8 text-center">
            <FolderKanban className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No projects yet
            </p>
            <p className="text-xs text-gray-400">
              Projects will appear here once created
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentProjects.map((project) => (
              <div
                key={project.project_id}
                className="flex items-center space-x-4 rounded-xl bg-gray-50/50 p-3 transition-colors hover:bg-gray-100/50 dark:bg-gray-800/50 dark:hover:bg-gray-700/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-orange-400 to-orange-600 text-white">
                  <FolderKanban className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {project.project_name}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center">
                      <Key className="mr-1 h-3 w-3" />
                      {project.stats?.total_api_keys || 0} keys
                    </span>
                    <span className="flex items-center">
                      <BarChart3 className="mr-1 h-3 w-3" />
                      {project.stats?.total_requests?.toLocaleString() ||
                        "0"}{" "}
                      requests
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                      project.status === "active"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
                    )}
                  >
                    {project.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex items-center justify-between">
          <a
            href="/admin/projects"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            View all projects
            <svg
              className="ml-1 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Showing {recentProjects.length} of {projects?.length || 0}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
